/** Konten bahasa Indonesia. Nama mode mengikuti katalog `id.json`. */
import type { LanguageContent } from '../registry.js';

export const ID: LanguageContent = {
  home: {
    intro: [
      'Retusch adalah permainan menggambar daring untuk 2 sampai 10 pemain, berjalan di peramban, tanpa pemasangan dan tanpa akun. Tiap ronde kamu menerima sebuah lukisan yang salah satu areanya dihapus: lukis ulang area itu supaya lenyap di dalam karyanya. Lalu peran bertukar, dan giliranmu mengendus retusan orang lain.',
      'Hampir semua permainan menggambar memberi hadiah kepada yang paling cepat dikenali. Retusch meminta kebalikannya: menang berarti tidak terlihat.',
    ],
    steps: [
      {
        title: 'Lukisan berlubang',
        text: 'Kamu menerima ilustrasi yang salah satu areanya dihapus.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'Kamu melukisnya ulang',
        text: 'Kamu menggambar ulang area itu supaya menyatu dengan karyanya.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'Perburuan',
        text: 'Lukisan kembali membawa retusan semua orang: temukan sebelum yang lain.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'Pengungkapan',
        text: 'Tiap persembunyian terbongkar, dan poin pun berjatuhan.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Perburuan bersama',
        text: 'Semua melukis di lukisan yang sama, masing-masing di areanya sendiri. Lalu perburuan dibuka untuk semua sekaligus: sebuah retusan hanya bisa ditemukan sekali, oleh orang yang pertama menyadarinya. Ini mode bawaan, mode yang selalu ada kejadiannya.',
      },
      {
        name: 'Stream',
        text: 'Tuan rumah tidak melukis: dia mencari sendirian, sementara semua orang lain bersembunyi di lukisannya. Dirancang untuk siaran langsung, di mana perburuan itulah tontonannya. Gratis selamanya.',
      },
      {
        name: 'Satu lukisan tiap pemain',
        text: 'Satu karya per pemain. Kamu mencari retusan satu orang saja, dengan sekali klik. Aturan paling sederhana di permainan ini, sekaligus yang paling menegangkan.',
      },
      {
        name: 'Satu orang melukis',
        text: 'Satu ronde, satu pelukis, semua yang lain memburu. Perannya bergilir, dan si pelukis menonton persembunyiannya jatuh atau bertahan.',
      },
      {
        name: 'Battle royale',
        text: 'Perburuan yang sama, tapi retusan yang pertama ketahuan menyingkirkan pembuatnya. Dimainkan sampai tersisa satu orang, tanpa menghitung satu poin pun.',
      },
      {
        name: 'Kontes',
        text: 'Tidak ada yang mencari. Semua melukis ulang area yang sama pada lukisan yang sama, lalu masing-masing memilih retusan yang paling disukainya. Tiap suara yang diterima bernilai satu poin.',
      },
    ],
    faq: [
      {
        question: 'Butuh berapa pemain?',
        answer:
          'Dari 2 sampai 10. Kalau kamu sendirian, satu lawan otomatis mengisi kursi kosong supaya permainan langsung mulai. Dari empat orang ke atas, perburuan bersama menunjukkan sisi terbaiknya.',
      },
      {
        question: 'Apakah gratis?',
        answer:
          'Ya. Permainan cepat, perburuan bersama, dan mode stream gratis, tanpa batas waktu dan tanpa batas jumlah permainan. Mode lainnya juga gratis untuk saat ini.',
      },
      {
        question: 'Perlu akun?',
        answer:
          'Tidak. Tulis nama, klik, main. Akun hanya menyimpan nama dan avatar dari satu permainan ke permainan berikutnya: sifatnya tetap pilihan.',
      },
      {
        question: 'Bisa di ponsel?',
        answer:
          'Bisa, antarmukanya menyesuaikan sentuhan: bilah alat terlipat, kaca pembesar di bawah jari, ketuk dua kali untuk memperbesar. Meski begitu, menggambar lebih presisi dengan tetikus dan di layar besar.',
      },
      {
        question: 'Bagaimana mengundang teman?',
        answer:
          'Buat sebuah ruang, salin tautan undangan, lalu tempel ke mana pun kamu mau. Temanmu mendarat langsung di meja, tanpa akun dan tanpa memasang apa pun.',
      },
      {
        question: 'Apa itu “retusan”?',
        answer:
          'Itulah yang kamu lukis di area yang dihapus. Kalau berhasil, ia disangka bagian dari lukisan aslinya dan tidak ada yang menemukannya. Kalau gagal, ia langsung mencolok mata.',
      },
    ],
    headings: {
      intro: 'Permainan menggambar yang menuntutmu menghilang',
      steps: 'Cara bermain',
      modes: 'Enam mode permainan',
      faq: 'Pertanyaan umum',
      more: 'Selengkapnya',
    },
    links: {
      rules: 'Baca aturan lengkap dan penilaiannya',
      modes: 'Bandingkan mode secara rinci',
    },
  },

  howToPlay: {
    title: 'Cara bermain Retusch',
    summary:
      'Aturan lengkap Retusch: jalannya satu ronde, penilaian di kedua sisi, alat menggambar, serta kiat untuk bersembunyi dan untuk mencari.',
    sections: [
      {
        heading: 'Idenya dalam tiga kalimat',
        blocks: [
          'Kamu diberi sebuah lukisan yang salah satu areanya dihapus, dan kamu melukis ulang area itu supaya lolos dari perhatian.',
          'Lalu semua orang memandangi lukisan yang sudah diretus dan mencari area yang dilukis ulang.',
          'Kamu meraih poin dengan menemukan cepat, dan meraih poin juga selama persembunyianmu bertahan.',
        ],
      },
      {
        heading: 'Jalannya satu ronde',
        blocks: [
          'Satu permainan merangkai ronde yang sama, lima ronde secara bawaan. Tiap ronde melewati tiga momen.',
          {
            list: [
              'Menggambar. Kamu menerima lukisan dan areamu, dan secara bawaan punya 2 menit untuk melukisnya ulang. Tombol Selesai berarti mengumpulkan: begitu semua sudah mengumpulkan, ronde berlanjut tanpa menunggu pewaktu.',
              'Perburuan. Lukisan muncul lagi bersama retusannya, dan secara bawaan kamu punya 30 detik untuk menyadarinya. Sekali klik pada area yang dilukis ulang akan membukanya untuk seluruh meja.',
              'Pengungkapan. Semua persembunyian dibingkai dan diberi nama, termasuk yang tidak dilihat siapa pun. Sebuah penggeser memungkinkanmu membandingkan lukisan yang diretus dengan karya aslinya.',
            ],
          },
        ],
      },
      {
        heading: 'Penilaian',
        blocks: [
          'Dua penghitung berjalan bersamaan dan saling berlawanan: yang mencari ingin cepat, yang melukis ingin areanya bertahan.',
          {
            list: [
              'Untuk yang mencari: 1000 poin kalau kamu menemukannya dalam 3 detik pertama, lalu turun merata sampai 100 poin di akhir tahap. Salah tebak atau tidak menemukan apa pun sama-sama tidak memberi apa-apa.',
              'Untuk yang melukis: hanya 100 poin kalau areamu langsung jatuh, lalu naik merata sampai 800 poin kalau ia bertahan sampai akhir. Retusan yang tidak ditemukan siapa pun bernilai maksimum, 800 poin.',
            ],
          },
          'Si pelukis dibayar sekali saja, saat pengungkapan, dan berdasarkan pencari tercepat. Jadi tidak jadi soal apakah kamu dicari dua orang atau delapan: yang dihitung hanya orang yang menemukanmu pertama.',
          'Penilaiannya sama berapa pun durasi tahap yang kamu atur. Memperpanjang perburuan tidak menggeser kedua ujungnya, hanya kemiringan di antaranya.',
          'Satu kesalahan saja per ronde. Klik pertama yang tidak mengenai retusan mana pun menutup perburuanmu: tanpa aturan ini, orang akan menyisir gambar asal-asalan sampai kebetulan kena. Dua tindakan tidak berbiaya apa-apa: mengeklik area sendiri, dan mengeklik retusan yang sudah ditemukan.',
        ],
      },
      {
        heading: 'Alat menggambar',
        blocks: [
          {
            list: [
              'Kuas (B): tiga ukuran cepat, plus penggeser untuk ukuran persis. Pintasan 1, 2, dan 3 memilih ketiga ukuran itu.',
              'Ember cat (P): mengisi bidang rata dalam sekali kerja, dan tidak pernah keluar dari areamu.',
              'Pipet (I): mengambil warna di titik mana pun pada lukisan. Menahan tombol Alt memindahkannya ke pipet selama satu klik, lalu mengembalikan alat sebelumnya.',
              'Tangan (H): menggeser tampilan. Di luar areamu, kuas dan ember cat melakukan hal yang sama, jadi kamu hampir tidak pernah perlu berganti alat.',
              'Batalkan (Ctrl+Z atau Cmd+Z): mundur satu goresan sekaligus, sampai tiga puluh kali. Tidak ada ulangi.',
              'Warna latar di ujung palet: itulah warna awal areamu. Melukis dengannya sama saja menghapus, bedanya ini tetap goresan biasa yang bisa dibatalkan dan ditimpa. Karena itulah tidak ada penghapus di permainan ini.',
            ],
          },
        ],
      },
      {
        heading: 'Lima kiat untuk bersembunyi',
        blocks: [
          {
            list: [
              'Ambil warna dengan pipet, jangan menerkanya. Pipet memberi warna persis dari sekitarnya: bidang yang meleset sedikit saja terlihat dari jauh, sedangkan yang persis jadi tidak kasatmata.',
              'Lanjutkan apa pun yang memotong areamu. Sebuah dinding, garis cakrawala, dahan yang masuk dari satu sisi harus keluar di sisi lain tepat pada tempatnya. Garis yang terputus adalah petunjuk pertama yang dicari pemain berpengalaman.',
              'Lukis sudut-sudutnya lebih dulu. Ini kesalahan paling umum: bagian tengah digarap rapi, empat sudutnya tertinggal pada latar awal, dan persegi itu terlihat dengan mata telanjang bahkan tanpa melihat gambarnya.',
              'Bergeraklah dari umum ke rinci. Letakkan dulu massa warna besar dengan kuas tebal, baru perhalus. Dibalik, seluruh waktumu habis di satu sudut sementara sisanya tetap kosong.',
              'Tiru tingkat kerincian sekitarnya. Area yang terlalu bersih di tengah bagian yang padat sama kelihatannya dengan area asal jadi di tengah bidang rata.',
            ],
          },
        ],
      },
      {
        heading: 'Empat kiat untuk mencari',
        blocks: [
          {
            list: [
              'Cari garis yang terputus. Sebuah tepi, sebuah kabel, sudut meja yang bergeser sehelai rambut: hampir selalu di situ tempatnya.',
              'Sapu dulu, baru perbesar. Dalam tampilan penuh, area yang menyatu buruk tampak seperti noda. Memperbesar itu untuk memastikan, bukan untuk mencari.',
              'Curigai yang terlalu mulus. Permukaan yang sempurna rata di tengah tekstur sama mencurigakannya dengan coretan asal jadi.',
              'Jangan mengeklik sembarangan. Kamu hanya berhak satu kali salah: lebih baik kehilangan tiga detik untuk memeriksa daripada kehilangan satu ronde penuh.',
            ],
          },
        ],
      },
      {
        heading: 'Lalu tergantung modenya?',
        blocks: [
          'Semua di atas menggambarkan perburuan bersama, mode bawaan. Mode lain mengubah siapa yang melukis, siapa yang mencari, dan bagaimana caranya menang: rincian tiap mode ada di halaman mode permainan.',
        ],
      },
    ],
  },

  modes: {
    title: 'Mode permainan Retusch',
    summary:
      'Enam cara bermain Retusch: perburuan bersama, stream, satu lukisan tiap pemain, satu orang melukis, battle royale, dan kontes. Jalannya, jumlah pemain yang disarankan, dan durasinya.',
    sections: [
      {
        heading: 'Perburuan bersama',
        blocks: [
          'Mode bawaan, dan mode yang sebaiknya dicoba lebih dulu.',
          'Semua melukis di lukisan yang sama, masing-masing di areanya sendiri. Ketika retusan sudah dikumpulkan, karyanya kembali utuh dan perburuan dibuka untuk semua sekaligus. Sebuah retusan hanya bisa ditemukan sekali, oleh orang yang pertama menyadarinya: saat itu ia tampil untuk seluruh meja dan keluar dari permainan. Kamu tidak pernah menemukan retusanmu sendiri, dan persembunyian yang tidak dilihat siapa pun memberi nilai maksimum kepada pembuatnya.',
          'Cocok untuk kelompok besar: tidak ada saat mati, semua mencari bersamaan, dan tiap temuan muncul di layar semua orang.',
          'Disarankan: 3 sampai 10 pemain.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'Bayangan cermin dari mode “satu orang melukis”, dirancang untuk siaran langsung.',
          'Semua pemain melukis di lukisan yang sama kecuali tuan rumah. Lalu hanya tuan rumah yang mencari, sementara penontonnya melihat dia kepayahan. Dia tidak dapat poin dan tidak masuk klasemen: permainannya berlangsung di antara mereka yang bersembunyi, dia memimpin perburuannya. Dia boleh salah sebanyak yang dia mau, aturan satu kesalahan tidak berlaku untuknya.',
          'Cocok untuk malam siaran langsung, dan karena alasan yang jelas: karena tuan rumah tidak melukis, penonton tidak pernah melihat persembunyiannya dibangun. Di mode lain mereka menyaksikan dia mengecat areanya sendiri, dan perburuan tidak lagi menyimpan rahasia bagi mereka.',
          'Disarankan: 4 sampai 10 pemain. Gratis, dan akan tetap begitu.',
        ],
      },
      {
        heading: 'Satu lukisan tiap pemain',
        blocks: [
          'Tiap pemain menerima karyanya sendiri dan menyembunyikan retusannya di sana. Saat perburuan, kamu diberi lukisan satu orang lain saja, dan kamu punya satu klik untuk menunjuk area yang dilukis ulang. Tidak ada balapan, tidak ada rebutan: hanya kamu, sebuah lukisan, dan satu keputusan.',
          'Cocok untuk berdua, atau ketika kalian ingin permainan tenang di mana masing-masing punya waktunya sendiri.',
          'Disarankan: 2 sampai 10 pemain.',
        ],
      },
      {
        heading: 'Satu orang melukis',
        blocks: [
          'Satu ronde, satu pelukis, semua yang lain memburu.',
          'Tiap ronde, hanya satu orang yang menerima lukisan. Yang lain menunggu, lalu mencari retusannya, masing-masing dinilai berdasarkan waktunya sendiri. Si pelukis dinilai berdasarkan pencari tercepat, dan melihat kembali persembunyiannya, membeku, selagi orang mencarinya. Perannya bergilir: di sini pengaturan ronde menghitung putaran penuh, sebab satu permainan baru adil kalau tiap orang melukis sama banyaknya.',
          'Cocok untuk kelompok kecil yang ingin beradu dalam kondisi setara, gambar demi gambar.',
          'Disarankan: 3 sampai 6 pemain, lebih dari itu menunggunya jadi lama.',
        ],
      },
      {
        heading: 'Battle royale',
        blocks: [
          'Perburuan bersama, dengan satu penyingkiran per ronde.',
          'Lukisan bersama yang sama, perburuan serentak yang sama. Tapi di sini tidak ada satu poin pun yang dihitung: satu-satunya pertanyaan adalah siapa yang tersisa. Pemain yang retusannya pertama ketahuan meninggalkan permainan. Yang lain tetap mencari sampai akhir ronde, dan itu tidak lagi menyingkirkan siapa pun, tapi memberi tiap orang peringkat rondenya, ditampilkan di areanya. Kalau tidak ada yang ditemukan, tidak ada yang tersingkir.',
          'Cocok untuk menutup malam: ketegangan naik seiring meja mengosong, dan ronde terakhir dimainkan berdua.',
          'Disarankan: 4 sampai 10 pemain.',
        ],
      },
      {
        heading: 'Kontes',
        blocks: [
          'Satu-satunya mode yang tidak ada pencariannya.',
          'Semua melukis ulang area yang sama pada lukisan yang sama. Lalu masing-masing menyusuri retusan orang lain, dengan iramanya sendiri, dan memilih yang menurutnya paling berhasil. Tiap suara yang diterima bernilai satu poin bagi pembuatnya. Selama pemungutan suara para pembuatnya anonim, dan tidak boleh memilih diri sendiri: nama dan suara baru keluar saat pengungkapan.',
          'Cocok ketika kelompoknya beragam, atau ketika ada yang lebih suka melukis tanpa tekanan pewaktu perburuan.',
          'Disarankan: 3 sampai 10 pemain.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Perburuan bersama', players: '3 sampai 10', duration: '15-20 mnt' },
    { name: 'Stream', players: '4 sampai 10', duration: '15-20 mnt' },
    { name: 'Satu lukisan tiap pemain', players: '2 sampai 10', duration: '15-20 mnt' },
    { name: 'Satu orang melukis', players: '3 sampai 6', duration: '20-35 mnt' },
    { name: 'Battle royale', players: '4 sampai 10', duration: '20-30 mnt' },
    { name: 'Kontes', players: '3 sampai 10', duration: '10-15 mnt' },
  ],
  modeTableHeadings: ['Mode', 'Pemain', 'Durasi'],
  premium: {
    heading: 'Mode gratis dan mode berbayar',
    blocks: [
      'Perburuan bersama dan mode stream gratis, secara permanen.',
      'Empat lainnya akan berbayar saat MEMBUAT sebuah permainan, dan hanya di situ. Bergabung akan selalu gratis: cukup satu orang di meja yang sudah membuka permainannya, dan semua bisa memainkan semua mode, tanpa akun dan tanpa membayar.',
      'Membukanya berupa pembelian sekali bayar, tanpa langganan, dan sekaligus menghilangkan iklan bagi akun yang membelinya. Sementara itu, semua mode terbuka untuk semua orang.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Apa itu Retusch?',
      date: '2026-08-21',
      summary:
        'Permainan menggambar daring untuk banyak orang, mirip Gartic Phone dari segi bentuk, tapi mekanikanya lebih mengingatkan pada Meccha Chameleon: alih-alih dikenali, kamu berusaha menghilang.',
      sections: [
        {
          heading: 'Permainan menggambar yang kalau dikenali berarti kalah',
          blocks: [
            'Retusch adalah permainan menggambar daring untuk 2 sampai 10 pemain. Tiap ronde kamu menerima ilustrasi yang salah satu areanya dihapus, dan kamu harus melukisnya ulang supaya menyatu dengan karyanya. Lalu semua orang memandangi lukisan yang sudah diretus dan mencari area yang dilukis ulang.',
            'Yang membedakannya muat dalam satu kalimat: menang berarti tidak terlihat.',
          ],
        },
        {
          heading: 'Yang sama dengan Gartic Phone',
          blocks: [
            'Bentuknya sama: kamu membuka tautan, menulis nama, lalu main. Tanpa akun, tanpa pemasangan, tanpa klien yang harus diunduh. Permainannya berjalan di peramban dan satu sesi mulai dalam beberapa detik. Kamu mengundang teman dengan menempelkan tautan ke sebuah percakapan, dan mejanya terisi.',
            'Iramanya pun terasa akrab: ronde pendek, sebuah pewaktu, tahap menggambar, lalu tahap saat ketahuan apa yang dibuat orang lain. Dan terutama pengungkapan bersama di akhir ronde, saat semua mengomentari apa yang mereka lihat. Kesenangan bersamanya sama persis.',
            'Seperti di Gartic Phone, kamu tidak perlu bisa menggambar. Sepotong langit yang diletakkan pas mengalahkan mahakarya yang meleber ke mana-mana.',
          ],
        },
        {
          heading: 'Yang diambil dari Meccha Chameleon',
          blocks: [
            'Namun di dasarnya, mekanikanya ada di tempat lain. Di Meccha Chameleon, permainan tiga dimensi di Steam, kamu mewarnai karaktermu sendiri lalu menempatkannya di sebuah latar, berharap tidak ada yang bisa membedakannya dari sekitarnya. Kemenangan datang dari kemiripan dan penempatan yang jitu, bukan dari aksi.',
            'Retusch mengambil gagasan itu dan mengembalikannya ke gambar. Kamu tidak memilih tempat bersembunyi, areanya diberikan permainan; sebagai gantinya, seluruh isi area itu kamu buat sendiri dari nol. Persembunyiannya berhasil kalau pandangan meluncur melewatinya tanpa berhenti.',
            'Dan seperti di setiap permainan penyamaran, ketegangannya lahir dari peran ganda. Kamu sekaligus yang bersembunyi dan yang memburu: sementara kamu menyisir lukisan mencari bidang yang mencurigakan, ada yang sedang menyisir milikmu.',
          ],
        },
        {
          heading: 'Yang tidak ada di tempat lain',
          blocks: [
            'Penilaiannya memberi hadiah kepada kedua kubu dengan arah berlawanan. Yang mencari makin banyak poinnya makin cepat dia menemukan; yang melukis makin banyak poinnya makin lama areanya bertahan. Retusan yang tidak dilihat siapa pun memberi nilai maksimum kepada pembuatnya.',
            'Dari situ lahir cara bermain yang sangat khas: kamu tidak berusaha membuatnya indah, kamu berusaha membuatnya persis. Mengambil warna yang tepat, memanjangkan garis tepat di tempatnya, tidak meninggalkan satu sudut pun kosong. Ini gerakan mengamati sebelum jadi gerakan menggambar.',
            'Untuk memahaminya dalam praktik, paling mudah membaca aturan lengkapnya, atau menjalankan satu permainan cepat: ia mulai tanpa pengaturan dan tanpa menunggu.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Cara menjadi tak terkalahkan di Retusch',
      date: '2026-08-21',
      summary:
        'Pintasan papan ketik, metode dari umum ke rinci, dan kesalahan sudut kosong: tiga hal yang memisahkan pemula dari pemain yang tidak bisa ditemukan lagi.',
      sections: [
        {
          heading: 'Hafalkan pintasannya dan waktu bergunamu berlipat dua',
          blocks: [
            'Dua menit menggambar lewat sangat cepat kalau tiap alat harus diambil dengan tetikus. Pintasan dengan mudah mengembalikan dua puluh atau tiga puluh detik per ronde, dan justru detik-detik itulah yang akan kamu curahkan untuk detail.',
            {
              list: [
                'B untuk kuas, P untuk ember cat, I untuk pipet, H untuk tangan.',
                '1, 2, dan 3 untuk tiga ukuran kuas, tanpa mampir ke bilah alat.',
                'Ctrl+Z, atau Cmd+Z di Mac, untuk membatalkan satu goresan sekaligus.',
                'Menahan tombol Alt memindahkan ke pipet dan mengembalikan alat sebelumnya begitu kamu melepasnya. Ini yang paling berguna di antara semuanya: kamu mengambil warna di tengah pekerjaan tanpa sekali pun berganti alat.',
              ],
            },
            'Satu catatan untuk papan ketik AZERTY: tombol 1, 2, dan 3 bekerja tanpa Shift, walaupun di papan ketikmu tertulis &, é, dan ". Permainan membaca posisi tombolnya, bukan karakter yang dihasilkannya.',
          ],
        },
        {
          heading: 'Lukis dari umum ke rinci',
          blocks: [
            'Kesalahan klasik pemula adalah mulai dari satu sudut lalu memoles sudut itu. Pewaktunya habis, tiga perempat areanya masih latar, dan retusannya terlihat dari sepuluh meter.',
            'Metode yang benar justru kebalikannya. Mulailah dengan kuas tebal, atau dengan ember cat, dan letakkan massa warna besar: langitnya, dindingnya, bayangan di tanah. Dalam dua puluh detik, areamu sudah punya kesan keseluruhan yang tepat dan tidak lagi terbaca sebagai blok asing.',
            'Baru setelah itu kecilkan ukurannya dan garap peralihannya: garis cakrawala yang memotong, tepi sebuah benda, gradasi yang berubah di tengah ketinggian. Kalau waktunya kurang, yang hilang adalah detailnya, bukan rondenya.',
            'Selalu ambil warna dengan pipet alih-alih memilihnya di palet. Nuansa yang kira-kira saja sudah cukup membongkar satu area penuh, sedangkan nuansa yang persis membuatnya tidak kasatmata.',
          ],
        },
        {
          heading: 'Jangan pernah membiarkan sudutnya kosong',
          blocks: [
            'Ini petunjuk nomor satu, dan yang pertama dicari pemain berpengalaman. Area dengan bagian tengah yang digarap rapi tapi keempat sudutnya tertinggal pada warna latar akan menggambar persegi panjang sempurna di layar. Bahkan tidak perlu melihat gambarnya: bentuk geometrisnya langsung mencolok mata.',
            'Biasakan menggarap tepinya dulu, baru bagian tengahnya. Bingkai yang menyatu rapi memaafkan bagian dalam yang kira-kira saja; sebaliknya tidak pernah benar.',
            'Sengajakan melebar melewati tepinya. Permainan memotong goresanmu di batas areanya, jadi kamu tidak berisiko apa-apa dengan pergi jauh lebih ke luar, dan itulah satu-satunya cara memastikan tidak ada satu piksel latar pun yang selamat di sudut.',
          ],
        },
        {
          heading: 'Dan ketika giliranmu mencari',
          blocks: [
            'Sapu dulu seluruh lukisan tanpa memperbesar: area yang menyatu buruk tampak seperti noda, dan memperbesar hanya berguna untuk memastikan. Lalu ikuti garis-garis panjang, yang memotong gambarnya: tepi yang bergeser sehelai rambut hampir selalu berupa retusan.',
            'Dan ingat bahwa kamu berhak satu kali salah per ronde. Tiga detik untuk memeriksa lebih murah daripada satu ronde penuh.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Permainan menggambar untuk malam bersama teman',
      date: '2026-08-21',
      summary:
        'Kenapa Retusch cocok untuk kelompok kecil, cara mengundang dengan menempelkan tautan, dan kenapa cukup satu pemain untuk membuka mode berbayar bagi seluruh meja.',
      sections: [
        {
          heading: 'Permainan yang tidak menuntut apa pun dari siapa pun',
          blocks: [
            'Penghalang terbesar dari malam bermain jarak jauh adalah pemasangan. Akun yang harus dibuat, klien yang harus diunduh, versi yang tidak cocok: separuh kelompok menyerah sebelum sempat main.',
            'Retusch berjalan di peramban. Temanmu mengeklik sebuah tautan, menulis nama, dan mendapati diri mereka sudah di meja. Tidak ada yang perlu dipasang, tidak ada yang perlu dibuat, dan ia berjalan baik di komputer maupun di ponsel.',
            'Satu hal lagi yang penting dalam kelompok: tidak ada yang tersisih karena tidak bisa menggambar. Permainan ini tidak meminta kamu menggambarkan sesuatu, hanya mencocokkan satu area dengan apa yang mengelilinginya. Yang gambarnya jelek tapi pengamatannya bagus sering menang.',
          ],
        },
        {
          heading: 'Mengundang dalam praktik',
          blocks: [
            'Buat sebuah ruang dari layar awal. Hilangkan centang “ruang publik” kalau kalian mau main di antara kalian saja: undangan lewat tautan lalu menjadi satu-satunya pintu masuk.',
            'Salin tautannya dengan tombol yang tersedia, lalu tempel ke obrolan grup kalian, ke kanal suara kalian, atau ke aplikasi pesan kalian. Di dalamnya ada kode ruangnya: temanmu mendarat langsung di mejamu.',
            'Kamu bisa mengatur permainannya sambil orang-orang berdatangan: modenya, jumlah rondenya, waktu menggambar, ukuran areanya, bahkan kategori gambarnya. Belum ada yang final sampai kamu menekan mulai.',
          ],
        },
        {
          heading: 'Satu membayar, semua bermain',
          blocks: [
            'Dua mode gratis selamanya: perburuan bersama, yaitu mode bawaan, dan mode stream.',
            'Yang lain akan berbayar, tapi hanya saat MEMBUAT sebuah permainan. Bergabung akan tetap gratis, tanpa syarat. Dalam praktiknya cukup satu orang dari kelompok yang sudah membuka permainannya, dan seluruh meja menikmati semua mode, tanpa akun dan tanpa membayar sepeser pun.',
            'Membukanya berupa pembelian sekali bayar, tanpa langganan.',
          ],
        },
        {
          heading: 'Mode apa untuk kelompok yang mana',
          blocks: [
            'Bertiga atau berempat, perburuan bersama sudah cukup dan langsung dipahami dalam satu ronde. Dari enam orang ke atas ia jadi benar-benar bagus: temuan datang beruntun dan layarnya tidak berhenti bergerak.',
            'Untuk menutup malam, cobalah battle royale: meja mengosong ronde demi ronde dan yang terakhir dimainkan berdua. Untuk kelompok yang beragam, di mana ada yang lebih suka menggambar santai, kontes menghapus seluruh tekanan pewaktu perburuan.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Permainan menggambar untuk siaran langsung',
      date: '2026-08-21',
      summary:
        'Retusch punya mode yang dirancang untuk siaran langsung, di mana streamer mencari sendirian sementara penontonnya bersembunyi. Cara memakainya, dan mode lain apa yang cocok di depan penonton.',
      sections: [
        {
          heading: 'Mode yang dibuat khusus',
          blocks: [
            'Mode stream membalik peran. Semua pemain melukis di lukisan yang sama kecuali tuan rumah, yang sama sekali tidak melukis. Lalu hanya tuan rumah yang mencari, sementara penontonnya menyaksikan.',
            'Inilah persis situasi yang dibutuhkan siaran langsung, dan karena alasan yang jelas: karena tuan rumah tidak melukis, persembunyiannya tidak ada, dan obrolan tidak melihat apa pun yang seharusnya tidak dilihat. Sebaliknya, penonton tahu persembunyian pemain lain, dan bisa menyaksikan dia melewatinya tiga kali berturut-turut.',
            'Untuk kasus ini ditulis dua aturan. Streamer tidak dapat poin dan tidak masuk klasemen: permainannya berlangsung di antara mereka yang bersembunyi, dia memimpin tontonannya. Dan dia boleh salah sebanyak yang dia mau, sementara semua mode lain berhenti pada kesalahan pertama. Siaran langsung tidak boleh berakhir gara-gara satu klik sial.',
            'Mode stream gratis, dan akan tetap begitu.',
          ],
        },
        {
          heading: 'Kontes, gagasan bagus kedua',
          blocks: [
            'Di mode kontes, semua melukis ulang area yang sama pada lukisan yang sama, lalu masing-masing memilih retusan yang paling disukainya. Tidak ada lagi yang harus ditemukan, hanya dinilai.',
            'Di depan penonton ia bekerja sangat baik, dan karena alasan yang sama dengan mode stream: tidak ada yang harus ditemukan, jadi tidak ada yang bisa dirusak obrolan. Karya-karyanya dibandingkan berdampingan, pemungutan suaranya memberi momen tegang, dan pengungkapan namanya menawarkan penutup yang tegas. Ia sekaligus mode yang paling pendek.',
          ],
        },
        {
          heading: 'Dan mode-mode lainnya',
          blocks: [
            'Tidak ada yang mewajibkan mode khusus, tapi kalau begitu lebih baik main dengan teman, bukan dengan obrolan. Di semua mode lain, streamer mengecat areanya sendiri di layar: penonton melihatnya dibangun dan tahu jawabannya bahkan sebelum perburuan dimulai.',
            'Sebaliknya, permainan antarteman sangat enak diikuti dari luar: tahap menggambar memperlihatkan tiap orang membangun persembunyiannya, dan pengungkapan membuka semua areanya sekaligus, lengkap dengan nama pembuatnya.',
          ],
        },
        {
          heading: 'Menarik obrolan masuk ke permainan',
          blocks: [
            'Tautan undangan satu-satunya yang perlu dibagikan. Tempel ke obrolan dan yang datang duluan akan memenuhi mejanya: tidak ada akun yang harus dibuat, tidak ada unduhan, tidak ada antrean.',
            'Batas jumlah pemain diatur di ruangnya, antara 2 dan 10. Dengan penonton yang banyak, buka beberapa permainan pendek alih-alih satu yang panjang: satu ronde berlangsung kurang dari tiga menit.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Yang baru di Retusch',
      date: '2026-08-21',
      summary:
        'Perubahan permainan yang masuk ke Retusch, dari yang terbaru sampai yang terlama: mode baru, aturan baru, perbaikan pada menggambar dan pada perburuan.',
      sections: [
        {
          heading: 'Agustus 2026',
          blocks: [
            {
              list: [
                'Area yang harus dilukis ulang tidak lagi berangkat dari persegi putih, melainkan dari warna dominan apa yang ditutupinya. Tidak lagi habis separuh waktu untuk mengecat ulang latar yang di lukisannya sendiri tidak pernah ada.',
                'Ember cat tidak lagi meninggalkan pinggiran terang di sepanjang goresan: kini ia menyelinap di bawah tepi mereka yang dilembutkan.',
                'Battle royale tidak lagi menghitung poin. Pemain yang pertama ketahuan meninggalkan permainan, dan tiap orang menerima peringkat ronde yang ditampilkan di areanya, dari merah ke hijau.',
                'Mode stream memberi streamer sebanyak apa pun percobaan yang dia mau: kesalahan pertama tidak lagi menutup rondenya.',
                'Di mode penonton, kini lukisan yang sedang kamu saksikan pembuatannya bisa digeser dan diperbesar.',
                'Reaksi emoji berangkat dari nama pengirimnya di daftar pemain, bukan lagi naik dari sisi bawah layar.',
                'Di luar area yang harus dilukis ulang, kuas dan ember cat menggeser tampilan: untuk berpindah tidak perlu lagi mengambil alat tangan.',
                'Nama permainan di kiri atas selama bermain akan membawa kembali ke halaman awal setelah konfirmasi.',
                'Bahasa Jepang dan Korea bergabung dengan lima belas bahasa yang sudah tersedia.',
              ],
            },
          ],
        },
        {
          heading: 'Juli 2026',
          blocks: [
            {
              list: [
                'Mode kontes baru: semua melukis ulang area yang sama, lalu masing-masing memilih retusan yang paling disukainya.',
                'Mode stream baru, dirancang untuk siaran langsung: tuan rumah mencari sendirian sementara penontonnya bersembunyi.',
                'Kategori gambar menggantikan tingkat kesulitan. Kamu mencentang apa yang ingin kamu lihat, dan tiap kategori memperlihatkan satu lukisan contoh.',
                'Dua ronde berturut-turut tidak lagi jatuh pada lukisan yang sama, dan kategori yang dicentang bergantian dari satu ronde ke ronde berikutnya.',
                'Galeri akhir permainan memungkinkanmu memutar ulang sebuah retusan goresan demi goresan dan mengekspornya sebagai gambar atau GIF animasi.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'Ringkasnya',
    allArticles: 'Semua artikel',
    theRules: 'Aturan permainan',
    theModes: 'Mode permainan',
  },
};
